import { useMemo, useState, useCallback, memo } from 'react';
import { Match, League } from '@/lib/types';
import { LeagueSection } from './LeagueSection';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';

interface VirtualizedMatchListProps {
  matches: Match[];
  leagues: League[];
  initialLimit?: number;
}

/**
 * Lazy-loaded match list that shows matches progressively.
 * Initially shows a limited number of leagues, with "Show More" for the rest.
 */
function VirtualizedMatchListComponent({ 
  matches, 
  leagues, 
  initialLimit = 5 
}: VirtualizedMatchListProps) {
  const [visibleCount, setVisibleCount] = useState(initialLimit);

  // Group matches by league - memoized
  const leagueMatches = useMemo(() => {
    const map = new Map<string, Match[]>();
    matches.forEach(match => {
      const leagueId = match.league_id || '';
      const existing = map.get(leagueId);
      if (existing) {
        existing.push(match);
      } else {
        map.set(leagueId, [match]);
      }
    });
    return map;
  }, [matches]);

  // Filter leagues that have matches - memoized
  const activeLeagues = useMemo(() => {
    return leagues.filter(league => {
      const leagueMatchList = leagueMatches.get(league.id);
      return leagueMatchList && leagueMatchList.length > 0;
    });
  }, [leagues, leagueMatches]);

  // Memoize visible leagues slice
  const { visibleLeagues, hasMore, remainingCount } = useMemo(() => ({
    visibleLeagues: activeLeagues.slice(0, visibleCount),
    hasMore: visibleCount < activeLeagues.length,
    remainingCount: activeLeagues.length - visibleCount,
  }), [activeLeagues, visibleCount]);

  // Stable callback
  const showMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + 5, activeLeagues.length));
  }, [activeLeagues.length]);

  if (activeLeagues.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      {visibleLeagues.map((league, index) => (
        <div 
          key={league.id}
          className="animate-fade-in"
          style={{ animationDelay: `${index * 40}ms` }}
        >
          <LeagueSection
            league={league}
            matches={leagueMatches.get(league.id) || []}
          />
        </div>
      ))}

      {hasMore && (
        <div className="flex justify-center pt-1.5 pb-2 sm:pt-2 sm:pb-3">
          <Button
            variant="outline"
            onClick={showMore}
            className="gap-2 h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm font-semibold border-border/80 hover:bg-secondary rounded-lg min-w-[160px] sm:min-w-[180px]"
          >
            <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Show {Math.min(5, remainingCount)} More
          </Button>
        </div>
      )}
    </div>
  );
}

export const VirtualizedMatchList = memo(VirtualizedMatchListComponent);
